'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useMemo, useRef } from 'react';
import { useDebounceValue, useResizeObserver } from 'usehooks-ts';
import AdSlot from '@/app/[locale]/(worlds)/[roomId]/components/AdSlot';
import PremiumTag from '@/app/[locale]/(worlds)/[roomId]/components/PremiumTag';
import WorldInfoCreator from '@/app/[locale]/(worlds)/[roomId]/components/WorldInfoCreator';
import WorldInfoDetails from '@/app/[locale]/(worlds)/[roomId]/components/WorldInfoDetails';
import WorldInfoTitle from '@/app/[locale]/(worlds)/[roomId]/components/WorldInfoTitle';
import WorldPlayButtonMobile from '@/app/[locale]/(worlds)/[roomId]/components/WorldPlayButtonMobile';
import { getMultisizePCAdSizes } from '@/constants/ad';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useUAContext } from '@/context/UAContext';
import { useWorldDetailsContext } from '@/context/WorldDetailsContext';
import { useEnableFull3d } from '@/hooks/useEnableFull3d';
import { usePlatformAdFree } from '@/hooks/usePlatformAdFree';
import { env } from '@/lib/runtime-env';
import { cn } from '@/lib/utils';
function WorldInfoPanel() {
  const $ = _c(21);
  const {
    isMobile,
    isMobileLandscape,
    isIosMobileApp
  } = useUAContext();
  const {
    isPremiumRoom
  } = useCheckEnterRoomContext();
  const {
    controlBarPresentation
  } = useWorldDetailsContext();
  const {
    isAdFree
  } = usePlatformAdFree();
  const enableFull3d = useEnableFull3d();
  const containerRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ref: containerRef as RefObject<HTMLElement>,
      box: "border-box"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    width: t1
  } = useResizeObserver(t0);
  const width = t1 === undefined ? 0 : t1;
  const [debouncedWidth] = useDebounceValue(width, 200);
  let t2;
  if ($[1] !== debouncedWidth) {
    t2 = getMultisizePCAdSizes(debouncedWidth);
    $[1] = debouncedWidth;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const eligibleAdSizes = t2;
  const shouldShowAd = !isAdFree && !enableFull3d && !isMobileLandscape && eligibleAdSizes.length > 0;
  let t3;
  if ($[3] !== controlBarPresentation.kind || $[4] !== isIosMobileApp || $[5] !== isPremiumRoom) {
    t3 = controlBarPresentation.kind !== "desktop" && <div className="flex flex-col gap-2">{isPremiumRoom && !isIosMobileApp && <PremiumTag className="border-vive-primary-70 border-[1.5px] bg-transparent" />}<WorldInfoTitle /></div>;
    $[3] = controlBarPresentation.kind;
    $[4] = isIosMobileApp;
    $[5] = isPremiumRoom;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== eligibleAdSizes || $[8] !== isMobile || $[9] !== shouldShowAd) {
    t4 = shouldShowAd && <AdSlot adSurface="content-detail-banner" gamAdUnitPath={env("NEXT_PUBLIC_GAM_MULTISIZE_PC_AD_UNIT_PATH")} sizes={eligibleAdSizes} contentClassName={cn(isMobile && "mx-auto")} className={cn(!isMobile && "block")} />;
    $[7] = eligibleAdSizes;
    $[8] = isMobile;
    $[9] = shouldShowAd;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isPremiumRoom) {
    t5 = isPremiumRoom && <WorldPlayButtonMobile />;
    $[11] = isPremiumRoom;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <WorldInfoCreator />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== isMobileLandscape) {
    t7 = isMobileLandscape && <WorldInfoDetails />;
    $[14] = isMobileLandscape;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t7) {
    t8 = <div ref={containerRef} data-component="world-info-panel" className="flex flex-col gap-4 px-4 lg:px-0">{t3}{t4}{t5}{t6}{t7}</div>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
export default WorldInfoPanel;